/**
 * Internal Subservices List Page
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    IconButton,
    Avatar,
    Chip,
} from '@mui/material';
import {
    Search as SearchIcon,
    Edit as EditIcon,
    Visibility as VisibilityIcon,
    Image as ImageIcon,
    ArrowBack as ArrowBackIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { PageHeader, DataTable, Column, ConfirmDialog } from '../../components/common';
import {
    useGetAllInternalSubServicesQuery,
    useGetServicesQuery,
    useGetSubServicesQuery,
    useDeleteInternalSubServiceMutation,
} from '../../store/api/servicesApi';
import { InternalSubService } from '../../types/service';
import { formatCurrency } from '../../utils/pricing';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const InternalSubservicesListPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [serviceFilter, setServiceFilter] = useState<number | ''>('');
    const [parentFilter, setParentFilter] = useState<number | ''>('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [selectedInternalSubservice, setSelectedInternalSubservice] = useState<InternalSubService | null>(null);

    const { data, isLoading, error } = useGetAllInternalSubServicesQuery({
        service: serviceFilter ? Number(serviceFilter) : undefined,
        parent: parentFilter ? Number(parentFilter) : undefined,
        search: search || undefined,
        is_active: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
        page: page + 1,
        page_size: rowsPerPage,
    });

    const { data: servicesData } = useGetServicesQuery({});
    const { data: subservicesData } = useGetSubServicesQuery({
        service: serviceFilter ? Number(serviceFilter) : undefined,
        page: 1,
        page_size: 1000,
    });

    const [deleteInternalSubService, { isLoading: isDeleting }] = useDeleteInternalSubServiceMutation();

    const handleDelete = async () => {
        if (!selectedInternalSubservice || !selectedInternalSubservice.parent_id) return;

        try {
            await deleteInternalSubService({
                subservice_id: selectedInternalSubservice.parent_id,
                id: selectedInternalSubservice.id,
            }).unwrap();
            toast.success(t('subservices.toast.internalDeleted'));
            setDeleteDialog(false);
            setSelectedInternalSubservice(null);
        } catch (err: any) {
            toast.error(err?.data?.message || t('subservices.toast.internalDeleteFailed'));
        }
    };

    const columns: Column<InternalSubService>[] = [
        {
            id: 'icon',
            label: t('subservices.cols.icon'),
            minWidth: 80,
            render: (row) => {
                const primaryIcon = row.icon || (row.icon_gallery && row.icon_gallery[0]) || undefined;
                return (
                    <Avatar
                        src={primaryIcon}
                        alt={row.name}
                        variant="rounded"
                        sx={{ width: 40, height: 40 }}
                    >
                        <ImageIcon />
                    </Avatar>
                );
            },
        },
        {
            id: 'name',
            label: t('subservices.cols.name'),
            minWidth: 150,
            render: (row) => (
                <Box
                    sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={() => navigate(`/internal-subservices/${row.id}`)}
                >
                    {row.name}
                </Box>
            ),
        },
        {
            id: 'service_name',
            label: t('subservices.cols.service'),
            minWidth: 120,
        },
        {
            id: 'parent_name',
            label: t('subservices.cols.parent'),
            minWidth: 150,
        },
        {
            id: 'description',
            label: t('subservices.cols.description'),
            minWidth: 200,
            render: (row) => (
                <Box
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 300,
                    }}
                >
                    {row.description}
                </Box>
            ),
        },
        {
            id: 'price',
            label: t('subservices.cols.price'),
            minWidth: 140,
            align: 'right',
            render: (row) => (
                row.uses_dual_pricing
                    ? `${formatCurrency(row.specialist_price)} / ${formatCurrency(row.consultant_price)}`
                    : formatCurrency(row.price)
            ),
        },
        {
            id: 'is_active',
            label: t('subservices.cols.status'),
            minWidth: 100,
            render: (row) => (
                <Chip
                    label={row.is_active ? t('subservices.status.ACTIVE') : t('subservices.status.INACTIVE')}
                    size="small"
                    color={row.is_active ? 'success' : 'default'}
                />
            ),
        },
        {
            id: 'actions',
            label: t('common.actions'),
            minWidth: 150,
            render: (row) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                        size="small"
                        onClick={() => navigate(`/internal-subservices/${row.id}/edit`)}
                        color="primary"
                        title={t('common.edit')}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => navigate(`/internal-subservices/${row.id}`)}
                        color="info"
                        title={t('subservices.buttons.viewDetails')}
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => {
                            setSelectedInternalSubservice(row);
                            setDeleteDialog(true);
                        }}
                        color="error"
                        title={t('common.delete')}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                    {row.parent_id && (
                        <IconButton
                            size="small"
                            onClick={() => navigate(`/subservices/${row.parent_id}`)}
                            color="secondary"
                            title={t('subservices.buttons.viewParent')}
                        >
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <PageHeader
                title={t('subservices.internalTitle')}
                subtitle={t('subservices.internalSubtitle')}
                breadcrumbs={[
                    { label: t('nav.dashboard'), path: '/' },
                    { label: t('nav.internalSubServices') },
                ]}
                actionLabel={t('subservices.buttons.addInternal')}
                onAction={() => navigate('/internal-subservices/create')}
            />

            {/* Filters */}
            <Card sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        placeholder={t('subservices.filters.searchInternal')}
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ minWidth: 250 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>{t('subservices.filters.service')}</InputLabel>
                        <Select
                            value={serviceFilter}
                            label={t('subservices.filters.service')}
                            onChange={(e) => {
                                setServiceFilter(e.target.value as number | '');
                                setParentFilter(''); // Reset parent when service changes
                            }}
                        >
                            <MenuItem value="">{t('subservices.filters.allServices')}</MenuItem>
                            {servicesData?.results?.map((service) => (
                                <MenuItem key={service.id} value={service.id}>
                                    {service.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 200 }} disabled={!serviceFilter}>
                        <InputLabel>{t('subservices.filters.parent')}</InputLabel>
                        <Select
                            value={parentFilter}
                            label={t('subservices.filters.parent')}
                            onChange={(e) => setParentFilter(e.target.value as number | '')}
                        >
                            <MenuItem value="">{t('subservices.filters.allParents')}</MenuItem>
                            {subservicesData?.results
                                ?.filter((s) => !s.parent_id) // Only top-level subservices
                                .map((subservice) => (
                                    <MenuItem key={subservice.id} value={subservice.id}>
                                        {subservice.name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>{t('subservices.filters.status')}</InputLabel>
                        <Select
                            value={statusFilter}
                            label={t('subservices.filters.status')}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <MenuItem value="">{t('subservices.filters.allStatuses')}</MenuItem>
                            <MenuItem value="active">{t('subservices.status.ACTIVE')}</MenuItem>
                            <MenuItem value="inactive">{t('subservices.status.INACTIVE')}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Card>

            {/* Table */}
            <DataTable
                columns={columns}
                data={data?.results || []}
                loading={isLoading}
                error={error ? t('subservices.loadErrorInternal') : null}
                page={page}
                rowsPerPage={rowsPerPage}
                totalCount={data?.count || 0}
                onPageChange={setPage}
                onRowsPerPageChange={setRowsPerPage}
                emptyMessage={t('subservices.empty.internal')}
            />

            <ConfirmDialog
                open={deleteDialog}
                onClose={() => {
                    setDeleteDialog(false);
                    setSelectedInternalSubservice(null);
                }}
                onConfirm={handleDelete}
                title={t('subservices.dialog.deleteInternalTitle')}
                message={t('subservices.dialog.deleteMessage', { name: selectedInternalSubservice?.name })}
                confirmText={t('common.delete')}
                cancelText={t('common.cancel')}
                confirmColor="error"
                loading={isDeleting}
            />
        </Box>
    );
};

export default InternalSubservicesListPage;

